import { Spacer } from "@components/atoms";
import { Modal, Platform } from "react-native";
import ModalAtom from "@components/atoms/Modal";
import { SpaceType } from "@components/atoms/Spacer/types";
import CustomWeb from "@components/atoms/Webview";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
// import Footer from "@scenes/pdp/utils/Footer";
import { Diamond } from "@src/types/DiamondDetail";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dimensions, StyleSheet, TouchableOpacity, View } from "react-native";
import { Image as ExpoImage } from "expo-image";
import { ICarouselInstance } from "react-native-reanimated-carousel";
import {
  getImageSource,
  getIOSImageProps,
  getOptimizedImageUrl,
  ImageSizePreset,
  toDrivePreviewUrl,
} from "@utils/image/imageOptimizer";
import styled, { css, useTheme } from "styled-components/native";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { GalleryCategory } from "../GalleryView/utils/GalleryType";
import OpenReportView from "../GalleryView/utils/OpenReport/OpenReportView";
import { RenderImageProps } from "../GalleryView/utils/types";
import GalleryIconsView from "./GalleryIconsView";
import { Portal, PortalHost } from "@gorhom/portal";
import { GALLERY_COLLECTION_OVERLAY_HOST } from "./constants";
import { VDBIcons } from "@utils/VDBIcons";
import Toast from "react-native-toast-message";
import { toastConfig } from "@components/molecules/VDBToast";
import { useAppSettings } from "@redux/slices/globalSlice";
import React from "react";
import { isJPGUrl } from "../GalleryView/utils/helpers";
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
  TouchableWithoutFeedback,
} from "react-native-gesture-handler";
import { MediaType } from "@utils/constants";
import ThreeDWebview from "@components/atoms/ThreeDWebview";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useThreeDProperties } from "../../../contexts/ThreeDPropertiesContext";
import { useSelector } from "react-redux";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import ShareModal from "../Share";
import { ShareType } from "../Share/types";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import {
  addThreeDAdditionalSettingsTypes,
  isPair,
  isThreeDEnabled,
} from "@utils/product/productValidators";
import CertificateIcon from "@components/atoms/CertificateIcon";
import {
  ResumableZoom,
  type ResumableZoomRefType,
} from "react-native-zoom-toolkit";

type MediaSize = { width: number; height: number };

const ZoomableMedia = ({
  enabled,
  resetKey,
  tapsEnabled = true,
  maxScale = 5,
  doubleTapScale = 1.8,
  children,
}: {
  enabled: boolean;
  resetKey: number | string;
  tapsEnabled?: boolean;
  maxScale?: number;
  doubleTapScale?: number;
  children: (size?: MediaSize) => React.ReactNode;
}) => {
  const zoomRef = useRef<ResumableZoomRefType>(null);
  const [size, setSize] = useState<MediaSize | null>(null);

  useEffect(() => {
    zoomRef.current?.reset(false);
  }, [resetKey]);

  const doubleTap = useMemo(
    () =>
      Gesture.Tap()
        .enabled(tapsEnabled)
        .numberOfTaps(2)
        .maxDuration(250)
        .runOnJS(true)
        .onEnd((e) => {
          const current = zoomRef.current?.getState()?.scale ?? 1;
          const next = current > 1 ? 1 : Math.min(doubleTapScale, maxScale);
          zoomRef.current?.zoom(next, { x: e.x, y: e.y });
        }),
    [tapsEnabled, doubleTapScale, maxScale]
  );

  if (!enabled) {
    return <>{children()}</>;
  }

  return (
    <GestureHandlerRootView
      style={{ flex: 1, width: "100%", height: "100%", overflow: "hidden" }}
      onLayout={(e) => {
        const { width, height } = e.nativeEvent.layout;
        setSize((prev) =>
          prev && prev.width === width && prev.height === height
            ? prev
            : { width, height }
        );
      }}
    >
      {size && size.width > 0 && size.height > 0 ? (
        <GestureDetector gesture={doubleTap}>
          <View
            style={{
              width: size.width,
              height: size.height,
              overflow: "hidden",
            }}
          >
            <ResumableZoom
              ref={zoomRef}
              minScale={1}
              maxScale={maxScale}
              pinchMode="clamp"
              tapsEnabled={false}
              style={{
                width: size.width,
                height: size.height,
                overflow: "hidden",
              }}
              {...({ scaleMode: "clamp" } as Record<string, unknown>)}
            >
              {children(size)}
            </ResumableZoom>
          </View>
        </GestureDetector>
      ) : null}
    </GestureHandlerRootView>
  );
};

type GalleryViewModalProps = {
  isOpen: boolean;
  onClose: (shareMediaUrl?: string) => void;
  ReceivedMedia: any;
  activeIndex: number;
  setActiveIndex: any;
  fromSearchResult: boolean;
  image_thumb_url?: string;
  productType?: string;
  itemDetail?: Diamond;
  isGuest?: any;
  imageWidth?: number;
  showThumbnails?: boolean;
  showSharableIcons?: boolean;
  hideNavigationArrow?: boolean;
  fromReviewPage?: boolean;
  isJewelryComponent?: boolean;
  isModal?: boolean;
};

const GalleryViewModal = ({
  isOpen,
  onClose,
  ReceivedMedia,
  activeIndex,
  setActiveIndex,
  image_thumb_url,
  productType,
  itemDetail,
  isGuest,
  showThumbnails = true,
  showSharableIcons = true,
  fromSearchResult = false,
  hideNavigationArrow = false,
  fromReviewPage,
  isJewelryComponent,
}: GalleryViewModalProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { initStatus } = useOrgSettingsSlice();
  const { threeDProperties, setThreeDProperties } = useThreeDProperties();
  const { addThreeDAdditionalSettings } = useJewelryConfiguratorState();

  const ImageComponent = ExpoImage;
  const [selectedIndex, setSelectedIndex] = useState(activeIndex);
  const { deviceWidth: viewWidth } = useIsFoldable();
  const width = viewWidth;
  const height = Dimensions.get("window").height;
  const [showShareModal, setshowShareModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  const getResponsiveContainerSize = (screenWidth: number) => {
    let containerWidth: number;

    if (screenWidth >= 2560) {
      containerWidth = 853;
    } else if (screenWidth >= 1440) {
      containerWidth = 800;
    } else if (screenWidth >= 1280) {
      containerWidth = 640;
    } else {
      containerWidth = Math.min(screenWidth * 0.8, 640);
    }

    const containerHeight = Math.floor(containerWidth * 0.75); // 4:3 ratio
    return { width: containerWidth, height: containerHeight };
  };

  const containerDimensions = useMemo(() => {
    return getResponsiveContainerSize(width);
  }, [width]);

  useEffect(() => {
    setSelectedIndex(activeIndex);
  }, [activeIndex]);

  const changeSelectedIndex = (i: any) => {
    if (bottomRef?.current) {
      if (i * (thumbSize + 10) - thumbSize / 2 > deviceWidth / 2) {
        bottomRef.current?.scrollTo({
          index: i,
          animated: true,
        });
      } else {
        bottomRef.current?.scrollTo({
          index: 0,
          animated: true,
        });
      }
    }
  };

  const [imageErrors, setImageErrors] = useState(() => {
    const initialErrors = {};
    ReceivedMedia?.forEach((media, index) => {
      initialErrors[index] = media?.link === "" ? true : false;
    });
    return initialErrors;
  });

  const getAppSettings = useAppSettings();
  const [visibilityTime, setVisibilityTime] = useState();
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  useEffect(() => {
    if (getAppSettings.toast_display_time) {
      setVisibilityTime(getAppSettings.toast_display_time);
    }
  }, [getAppSettings]);

  let isSmallDeviceWidth = !theme.isMobile ? true : false;
  const [deviceWidth] = useState(viewWidth);
  let resizeMode = "contain";
  let applyRadius = false;
  let resizeImage = false;
  let extraSmallImage = false;
  let isExternalView = GalleryCategory.ModelView;
  let showMediaModel = false;
  let GIACertEnable = true;
  let thumbSize = theme.isLargeScreen ? 80 : 56;
  const bottomRef = useRef<ICarouselInstance>(null);

  let thumbColor = theme["core-06"];
  let thumbDefaultBorderColor = theme["core-03"];
  let thumbResizeMode = "cover";

  const isPdfUrl = (url: string) => {
    const lowerCaseUrl = url?.toLowerCase();
    return !lowerCaseUrl?.includes(".pdf");
  };

  const isGemprintUrl = (url: string) => {
    if (!url) return false;
    return url.toLowerCase().includes("gemprint");
  };

  const preFix =
    Platform.OS === "android"
      ? "https://docs.google.com/gview?embedded=true&url="
      : "";
  const loadingUrlExternalView =
    ReceivedMedia?.[selectedIndex]?.type === MediaType.PDF &&
    (isPdfUrl(ReceivedMedia?.[selectedIndex]?.link) || showMediaModel)
      ? true
      : false;

  const three_d_settings = useSelector(
    (state: any) => state.globalData?.app_settings?.three_d
  );

  const leftItemLeftClickEvent = () => {
    if (activeIndex !== 0) {
      setActiveIndex(activeIndex - 1);
      setSelectedIndex(activeIndex - 1);
      changeSelectedIndex(activeIndex - 1);
    }
  };

  const leftItemRightClickEvent = () => {
    if (activeIndex < ReceivedMedia?.length - 1) {
      setActiveIndex(activeIndex + 1);
      setSelectedIndex(activeIndex + 1);
      changeSelectedIndex(activeIndex + 1);
    }
  };

  const renderThumb = ({ item, index }: RenderImageProps) => {
    let imageUri = "";

    if (
      item?.type === MediaType.IMAGE ||
      item?.type === MediaType.VIDEO ||
      (item?.type === "pdf" && item?.thumb)
    ) {
      imageUri = item?.thumb ? item?.thumb : image_thumb_url;

      if (imageUri === "" || imageUri === undefined) {
        setImageErrors((prevErrors) => ({
          ...prevErrors,
          [index]: true,
        }));
      }
    }

    const handleError = () => {
      setImageErrors((prevErrors) => ({
        ...prevErrors,
        [index]: true,
      }));
    };

    const scrollToIndex = (i: number) => {
      setActiveIndex(i);
      setSelectedIndex(i);
      changeSelectedIndex(i);
    };

    return (
      <View key={index}>
        <TouchableOpacity
          onPress={() => scrollToIndex(index)}
          activeOpacity={0.8}
        >
          <View>
            {item?.type === MediaType.IMAGE ||
            item?.type === MediaType.VIDEO ||
            (item?.type === "pdf" && item?.thumb) ? (
              !imageErrors?.[index] ? (
                <ImageComponent
                  contentFit={thumbResizeMode}
                  source={getImageSource(
                    getOptimizedImageUrl(imageUri, ImageSizePreset.PDP_THUMB),
                    ImageSizePreset.PDP_THUMB
                  )}
                  {...getIOSImageProps()}
                  onError={handleError}
                  style={[
                    styles.thumb,
                    {
                      borderColor:
                        selectedIndex === index
                          ? thumbColor
                          : thumbDefaultBorderColor,
                    },
                    { width: thumbSize, height: thumbSize },
                  ]}
                />
              ) : (
                <View
                  style={[
                    styles.thumb,
                    {
                      borderColor:
                        selectedIndex === index
                          ? thumbColor
                          : thumbDefaultBorderColor,
                    },
                    { width: thumbSize, height: thumbSize },
                  ]}
                >
                  <EmptyImagePreview
                    shape={getShapeValue(itemDetail)}
                    size={40}
                    isPair={isPair(itemDetail) ?? false}
                  />
                </View>
              )
            ) : item.type === MediaType.MODEL_3D ? (
              <View
                style={[
                  styles.thumb,
                  {
                    borderColor:
                      activeIndex === index
                        ? thumbColor
                        : thumbDefaultBorderColor,
                    backgroundColor: theme["core-01"],
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "center",
                  },
                  { width: thumbSize, height: thumbSize },
                ]}
              >
                <VDBIcon
                  name={three_d_settings?.icon?.data || VDBIcons.THREE_D}
                  uri={three_d_settings?.icon?.data}
                  size={34}
                  thumbSize={thumbSize}
                  color={theme["core-06"]}
                  variant={
                    three_d_settings?.icon?.type === IconsType.img_url
                      ? IconsVariant.CustomUrl
                      : IconsVariant.VDBCustom
                  }
                />
              </View>
            ) : (
              <View
                style={[
                  styles.thumb,
                  {
                    borderColor:
                      selectedIndex === index
                        ? thumbColor
                        : thumbDefaultBorderColor,
                    backgroundColor: theme["core-02"],
                    justifyContent: "center",
                    alignContent: "center",
                    alignItems: "center",
                  },
                  { width: thumbSize, height: thumbSize },
                ]}
              >
                <CertificateIcon
                  mediaType={item?.type}
                  labId={itemDetail?.lab}
                  size={60}
                  color={theme["core-06"]}
                />
              </View>
            )}
            {item?.type === MediaType.VIDEO && (
              <VideoContainer thumbSize={thumbSize}>
                <VDBIcon
                  name={"play"}
                  size={24}
                  color={theme["core-01"]}
                  variant={IconsVariant.VDBCustom}
                />
              </VideoContainer>
            )}
          </View>
        </TouchableOpacity>
      </View>
    );
  };

  const imageComponent = useMemo(() => {
    if ((ReceivedMedia?.length ?? 0) === 0) {
      return (
        <EmptyImagePreview
          shape={getShapeValue(itemDetail)}
          size={extraSmallImage ? 100 : 300}
          extraSmallImage={extraSmallImage}
          isExternalView={isExternalView}
          isPair={isPair(itemDetail) ?? false}
        />
      );
    }

    let threeDMedia = itemDetail?.three_d_media || [];
    if (isThreeDEnabled(itemDetail) && threeDMedia) {
      threeDMedia = addThreeDAdditionalSettings(
        threeDMedia,
        addThreeDAdditionalSettingsTypes.morph
      );
    }

    const threeDMediaObject = {
      three_d_data_type:
        initStatus?.three_d_settings?.three_d_data_type || "webgi",
      functionName: "init",
      params: {
        data: threeDMedia || [],
        vjson: initStatus?.three_d_settings?.vjson,
      },
    };

    const injectedCSS = `
    .divbtnpos,.hPHUXe, .sliderHeight {
      display: none !important;
    }
  `;
    const isJPGImage = isJPGUrl(ReceivedMedia?.[selectedIndex]?.link);
    const currentLink = ReceivedMedia?.[selectedIndex]?.link;
    const fitMode = (
      currentLink
        ? isJPGImage && ReceivedMedia[selectedIndex]?.type === MediaType.PDF
          ? "contain"
          : resizeMode
        : "contain"
    ) as "cover" | "contain" | "fill" | "none" | "scale-down";
    const optimizedModalUri = getOptimizedImageUrl(
      currentLink,
      ImageSizePreset.PDP_MAIN
    );
    const isWeb = Platform.OS === "web";
    return ReceivedMedia?.[selectedIndex]?.type === MediaType.IMAGE ||
      isJPGImage ? (
      !imageErrors?.[selectedIndex] ? (
        <ZoomableMedia enabled={!isWeb} resetKey={selectedIndex}>
          {(zoomSize) => (
            <ImageComponent
              contentFit={fitMode}
              source={getImageSource(
                optimizedModalUri,
                ImageSizePreset.PDP_MAIN
              )}
              {...getIOSImageProps()}
              onError={() =>
                setImageErrors((prevErrors) => ({
                  ...prevErrors,
                  [selectedIndex]: true,
                }))
              }
              style={
                zoomSize
                  ? { width: zoomSize.width, height: zoomSize.height }
                  : [
                      isSmallDeviceWidth
                        ? styles.smallimage(
                            deviceWidth,
                            applyRadius,
                            resizeImage,
                            false
                          )
                        : null,
                      resizeImage
                        ? !theme.isMobile
                          ? styles.smallimage(
                              600,
                              applyRadius,
                              resizeImage,
                              true
                            )
                          : styles.galleryimage
                        : extraSmallImage
                        ? undefined
                        : styles.largeimage,
                    ]
              }
            />
          )}
        </ZoomableMedia>
      ) : (
        <EmptyImagePreview
          shape={getShapeValue(itemDetail)}
          size={extraSmallImage ? 100 : 300}
          extraSmallImage={extraSmallImage}
          isExternalView={isExternalView}
          isPair={isPair(itemDetail) ?? false}
        />
      )
    ) : loadingUrlExternalView ? (
      <OpenReportView
        resizeImage={extraSmallImage}
        url={ReceivedMedia?.[selectedIndex]?.link}
        showMediaModel={showMediaModel}
        mediaType={ReceivedMedia?.[selectedIndex]?.type}
        itemID={itemDetail?.id}
        certNum={itemDetail?.cert_num}
        GIACertEnable={GIACertEnable}
        linkedPairChildren={itemDetail?.children}
      />
    ) : ReceivedMedia?.[selectedIndex]?.type === MediaType.MODEL_3D ? (
      <CustomWebContainer>
        <View
          style={{
            flex: 1,
            width: "100%",
            height: !theme.isMobile ? "100%" : "85%",
          }}
        >
          <ThreeDWebview
            isSmallDeviceWidth={isSmallDeviceWidth}
            deviceWidth={deviceWidth}
            resizeImage={resizeImage}
            url={ReceivedMedia?.[selectedIndex]?.link}
            isFromPDP={false}
            extraSmallImage={extraSmallImage}
            enableReload={false}
            allowScaleScript={false}
            showLoader={false}
            mediaType={ReceivedMedia?.[selectedIndex]?.type}
            isClickable={true}
            injectJavaScript={threeDMediaObject}
            iframeKey={"vdb-overlay-iframe"}
            threeDProperties={threeDProperties}
            setThreeDProperties={setThreeDProperties}
          />
        </View>
      </CustomWebContainer>
    ) : (
      <CustomWebContainer>
        <View
          style={{
            width: "100%",
            height: !theme.isMobile ? "100%" : "85%",
          }}
        >
          <CustomWeb
            isSmallDeviceWidth={isSmallDeviceWidth}
            deviceWidth={deviceWidth}
            resizeImage={resizeImage}
            url={
              ReceivedMedia?.[selectedIndex]?.type === MediaType.PDF
                ? preFix + ReceivedMedia?.[selectedIndex]?.link
                : toDrivePreviewUrl(ReceivedMedia?.[selectedIndex]?.link)
            }
            isFromPDP={false}
            extraSmallImage={extraSmallImage}
            enableReload={
              ReceivedMedia?.[selectedIndex]?.type === MediaType.PDF
            }
            allowScaleScript={false}
            showLoader={true}
            mediaType={ReceivedMedia?.[selectedIndex]?.type}
            videoType={ReceivedMedia[selectedIndex]?.video_type}
            injectedCSS={injectedCSS}
          />
        </View>
      </CustomWebContainer>
    );
  }, [
    ReceivedMedia?.[selectedIndex]?.link,
    ReceivedMedia?.[selectedIndex]?.type,
    resizeMode,
    imageErrors?.[selectedIndex],
    setImageErrors,
    selectedIndex,
    itemDetail?.id,
    threeDProperties,
  ]);

  return (
    <>
      {Platform.OS === "web" && theme.isLargeScreen ? (
        <Modal
          animationType="slide"
          transparent={true}
          visible={isOpen}
          supportedOrientations={["landscape", "portrait"]}
        >
          <CenteredView>
            <ModalView softShadow="1">
              <TouchableWithoutFeedback onPress={() => setShareMenuOpen(false)}>
                <StyledBackdropView>
                  <ClosedView onPress={() => onClose()}>
                    <ClosedContainerView hardShadow="1">
                      <VDBIcon
                        name={VDBIcons.CLOSE_CIRCLE}
                        size={24}
                        color={theme["core-06"]}
                        variant={IconsVariant.VDBCustom}
                      />
                    </ClosedContainerView>
                  </ClosedView>
                  <Container
                    isGemprint={isGemprintUrl(
                      ReceivedMedia?.[activeIndex]?.link
                    )}
                    alignCenter={!showSharableIcons && !showThumbnails}
                  >
                    {showThumbnails && (
                      <ThumbContainer>
                        <ThumbnailsCarousel
                          showsHorizontalScrollIndicator={false}
                        >
                          {ReceivedMedia?.map((item: any, index: number) =>
                            renderThumb({ item, index })
                          )}
                        </ThumbnailsCarousel>
                      </ThumbContainer>
                    )}
                    <ImageParentContainer>
                      {!hideNavigationArrow && (
                        <View style={{ pointerEvents: "auto" }}>
                          <TouchableOpacity
                            onPress={() => leftItemLeftClickEvent()}
                            activeOpacity={0.8}
                          >
                            <IconContainer>
                              <VDBIcon
                                name={VDBIcons.ARROW_LEFT}
                                size={32}
                                color={theme["core-06"]}
                                variant={IconsVariant.VDBCustom}
                              />
                            </IconContainer>
                          </TouchableOpacity>
                        </View>
                      )}
                      <ImageContainer
                        width={theme.isLargeScreen ? width / 3 : width}
                        height={theme.isTablet ? height / 1.7 : width}
                        containerWidth={containerDimensions.width}
                        containerHeight={containerDimensions.height}
                        isGemprint={isGemprintUrl(
                          ReceivedMedia?.[activeIndex]?.link
                        )}
                        windowWidth={width}
                      >
                        <MainContainer
                          type={ReceivedMedia?.[activeIndex]?.type}
                          width={theme.isLargeScreen ? width / 3 : width}
                          height={theme.isTablet ? height / 1.7 : width}
                          containerWidth={containerDimensions.width}
                          containerHeight={containerDimensions.height}
                          isGemprint={isGemprintUrl(
                            ReceivedMedia?.[activeIndex]?.link
                          )}
                          windowWidth={width}
                        >
                          {imageComponent}
                        </MainContainer>
                      </ImageContainer>
                      {!hideNavigationArrow && (
                        <View style={{ pointerEvents: "auto" }}>
                          <TouchableOpacity
                            onPress={() => leftItemRightClickEvent()}
                            activeOpacity={0.8}
                          >
                            <IconContainer>
                              <VDBIcon
                                name={VDBIcons.ARROW_RIGHT}
                                size={32}
                                color={theme["core-06"]}
                                variant={IconsVariant.VDBCustom}
                              />
                            </IconContainer>
                          </TouchableOpacity>
                        </View>
                      )}
                    </ImageParentContainer>
                    {showSharableIcons ? (
                      <GalleryIconsView
                        isOpen={isOpen}
                        productType={productType}
                        itemDetail={itemDetail}
                        fromPDPScreen={true}
                        showExpand={true}
                        ReceivedMedia={ReceivedMedia}
                        activeIndex={activeIndex}
                        closeViewModal={onClose}
                        setActiveIndex={setActiveIndex}
                        isGuest={isGuest}
                        shareMenuOpen={shareMenuOpen}
                        setShareMenuOpen={setShareMenuOpen}
                        fromReviewPage={fromReviewPage}
                        setshowShareModal={setshowShareModal}
                        setSelectedOption={setSelectedOption}
                        isJewelryComponent={isJewelryComponent}
                        isModal={true}
                      />
                    ) : (
                      <></>
                    )}
                  </Container>
                </StyledBackdropView>
              </TouchableWithoutFeedback>
            </ModalView>
          </CenteredView>
          <Toast
            config={toastConfig}
            visibilityTime={visibilityTime ? Number(visibilityTime) : 5000}
          />
        </Modal>
      ) : (
        <ModalAtom
          headerTitle={t(
            fromSearchResult
              ? "searchResult.popOverView.title.text"
              : "productDetailPage.popup.title.text"
          )}
          isOpen={isOpen}
          onClose={() => onClose()}
          paddingBottom={Platform.OS === "android" ? 70 : 0}
          paddingLeft={0}
          paddingTop={0}
          paddingRight={0}
          size="lg"
          enableToastMessage={true}
          allowFullScreen={theme.isTablet ? true : false}
          height={"95%"}
          skipKeyboardAware={true}
        >
          <Container
            isGemprint={isGemprintUrl(ReceivedMedia?.[activeIndex]?.link)}
            alignCenter={!showSharableIcons && !showThumbnails}
          >
            {showSharableIcons && (
              <GalleryIconsView
                isOpen={isOpen}
                productType={productType}
                itemDetail={itemDetail}
                fromPDPScreen={true}
                showExpand={true}
                ReceivedMedia={ReceivedMedia}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                closeViewModal={onClose}
                isGuest={isGuest}
                shareMenuOpen={shareMenuOpen}
                setShareMenuOpen={setShareMenuOpen}
                fromReviewPage={fromReviewPage}
                setshowShareModal={setshowShareModal}
                setSelectedOption={setSelectedOption}
                isJewelryComponent={isJewelryComponent}
                isModal={true}
              />
            )}
            <ImageContainer
              width={theme.isLargeScreen ? width / 3 : width}
              height={theme.isTablet ? height / 1.7 : width}
              containerWidth={containerDimensions.width}
              containerHeight={containerDimensions.height}
              isGemprint={isGemprintUrl(ReceivedMedia?.[activeIndex]?.link)}
              windowWidth={width}
            >
              <MainContainer
                type={ReceivedMedia?.[activeIndex]?.type}
                width={theme.isLargeScreen ? width / 3 : width}
                height={theme.isTablet ? height / 1.7 : width}
                containerWidth={containerDimensions.width}
                containerHeight={containerDimensions.height}
                isGemprint={isGemprintUrl(ReceivedMedia?.[activeIndex]?.link)}
                windowWidth={width}
              >
                {imageComponent}
              </MainContainer>
            </ImageContainer>
            {showThumbnails && (
              <ThumbContainer>
                <ThumbnailsCarousel
                  thumbSize={thumbSize}
                  horizontal={true}
                  showsHorizontalScrollIndicator={false}
                >
                  {ReceivedMedia?.map((item: any, index: number) =>
                    renderThumb({ item, index })
                  )}
                </ThumbnailsCarousel>
              </ThumbContainer>
            )}
            {showShareModal &&
              (Platform.OS !== "web" && !theme.isLargeScreen ? (
                <Portal hostName={GALLERY_COLLECTION_OVERLAY_HOST}>
                  <ShareModal
                    isOpen={showShareModal}
                    onClose={() => setshowShareModal(false)}
                    item={itemDetail}
                    shareType={ShareType.singleShare}
                    selectedOption={selectedOption}
                    inWindow={true}
                  />
                </Portal>
              ) : (
                <ShareModal
                  isOpen={showShareModal}
                  onClose={() => setshowShareModal(false)}
                  item={itemDetail}
                  shareType={ShareType.singleShare}
                  selectedOption={selectedOption}
                />
              ))}
          </Container>
          <Spacer size={SpaceType.Big} />
          {theme.isMobile && Platform.OS !== "web" && (
            <Spacer size={SpaceType.ExtraLarge} />
          )}
          {/* Full-window host for the in-window collection picker teleported
              from the FavoriteButton inside GalleryIconsView. Keeps the picker
              in this modal's window instead of a nested RN Modal. */}
          <PortalHost name={GALLERY_COLLECTION_OVERLAY_HOST} />
        </ModalAtom>
      )}
      {Platform.OS === "web" && theme.isLargeScreen && showShareModal && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setshowShareModal(false)}
          item={itemDetail}
          shareType={ShareType.singleShare}
          selectedOption={selectedOption}
        />
      )}
    </>
  );
};

const VideoContainer = styled.View<any>`
  border-radius: 12px;
  position: absolute;
  align: center;
  align-items: center;
  justify-content: center;
  height: ${({ thumbSize }) => thumbSize}px;
  width: ${({ thumbSize }) => thumbSize}px;
  background-color: ${({ theme }) => theme["over-02"]};
`;

const styles = StyleSheet.create({
  containerStyle: {
    height: "100%",
    width: "100%",
  },
  centercontainerStyle: {
    height: "100%",
    width: "100%",
    alignItems: "center",
  },
  largeimage: {
    height: "100%",
    width: "100%",
  },
  tabletContainerStyle: {
    height: "100%",
    width: "100%",
  },
  galleryimage: {
    justifyContent: "center",
    alignItems: "center",
  },
  smallimage: (
    deviceWidth: number,
    applyRadius: boolean,
    resizeImage: boolean,
    applyPercent: boolean
  ) => ({
    height: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    width: applyPercent ? "100%" : resizeImage ? deviceWidth / 2 : deviceWidth,
    borderRadius: applyRadius ? 12 : 0,
    paddingBottom: "20px",
    overflow: "hidden",
  }),
  header: {
    position: "absolute",
    top: 0,
    width: "100%",
  },
  footer: {
    bottom: 0,
    position: "absolute",
    width: "100%",
  },
  activeThumb: {
    borderWidth: 3,
  },
  thumb: {
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
  },
  thumbnailListContainer: {
    paddingHorizontal: 10,
  },
  bottomFlatlist: {
    position: "absolute",
  },
});
const MainContainer = styled.View<any>`
  height: ${({
    theme,
    width,
    height,
    type,
    isGemprint,
    windowWidth,
    containerHeight,
  }) => {
    if (isGemprint) {
      if (theme.isMobile) {
        return `${width}px`;
      }

      const baseWidth = theme.isTablet ? width : windowWidth * 0.8;
      const gemprintWidth = Math.min(baseWidth, 1000);
      const gemprintHeight = Math.min(Math.floor(gemprintWidth * 0.7), 700);
      return `${gemprintHeight}px`;
    }
    return theme.isMobile
      ? type === "video" && Platform.OS !== "web"
        ? `${width + 74}px`
        : `${width}px`
      : theme.isLargeScreen
      ? `${containerHeight ?? width}px`
      : `${height}px`;
  }};
  width: ${({
    theme,
    type,
    width,
    height,
    isGemprint,
    windowWidth,
    containerWidth,
  }) => {
    if (isGemprint) {
      if (theme.isMobile) {
        return `${width}px`;
      }
      const baseWidth = theme.isTablet ? width : windowWidth * 0.8;
      const gemprintWidth = Math.min(baseWidth, 1000);
      return `${gemprintWidth}px`;
    }
    return theme.isMobile
      ? type === "video" && Platform.OS !== "web"
        ? `${width + 74}px`
        : `${width}px`
      : theme.isLargeScreen
      ? `${containerWidth ?? width}px`
      : `${height}px`;
  }};
  background-color: ${({ theme, imageError }) =>
    imageError ? theme["core-03"] : "transparent"};
  ${({ isGemprint }) =>
    isGemprint &&
    css`
      display: flex;
      justify-content: center;
      align-items: center;
    `}
`;

const Container = styled.View<{ isGemprint?: boolean; alignCenter: boolean }>`
  flex: 1;
  flex-basis: auto;
  flex-direction: ${({ theme }) => (theme.isLargeScreen ? "row" : "column")};
  height: 100%;
  justify-content: ${({ theme, alignCenter }) =>
    theme.isLargeScreen
      ? alignCenter
        ? "center"
        : "space-between"
      : "center"};
  margin: ${({ theme }) => (theme.isLargeScreen ? "0 40px" : "0")};
  ${({ theme }) =>
    Platform.OS === "web" &&
    !theme.isMobile &&
    css`
      min-height: 450px;
      margin-top: 0px;
      flex-shrink: 0;
      align-items: center;
    `}
  ${({ isGemprint }) =>
    isGemprint &&
    css`
      overflow: visible;
    `}
`;

const CustomWebContainer = styled.View`
  flex: 1;
  flex-basis: auto;
  flex-direction: ${({ theme }) => (theme.isLargeScreen ? "row" : "column")};
  justify-content: ${({ theme }) =>
    theme.isLargeScreen ? "space-between" : "flex-start"};
  margin: 0;
  ${({ theme }) =>
    Platform.OS === "web" &&
    !theme.isMobile &&
    css`
      min-height: 450px;
      margin-top: 0px;
      flex-shrink: 0;
      align-items: center;
    `}
`;

const ImageContainer = styled.View<any>`
  flex: 1;
  margin: 0;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme["core-02"]};
  width: ${({ theme, containerWidth, width, isGemprint, windowWidth }) => {
    if (containerWidth && Platform.OS === "web" && theme.isLargeScreen) {
      return `${containerWidth ?? width}px`;
    }
    if (isGemprint) {
      if (theme.isMobile) {
        return `${width}px`;
      }
      const baseWidth = theme.isTablet ? width : (windowWidth || width) * 0.8;
      const gemprintWidth = Math.min(baseWidth, 1000);
      return `${gemprintWidth}px`;
    }
    return !theme.isMobile ? width : "100%";
  }};
  height: ${({
    containerHeight,
    width,
    height,
    type,
    theme,
    isGemprint,
    windowWidth,
  }) => {
    if (containerHeight && theme.isLargeScreen && Platform.OS === "web") {
      return `${containerHeight ?? height}px`;
    }
    if (isGemprint) {
      if (theme.isMobile) {
        return `${width}px`;
      }

      const baseWidth = theme.isTablet ? width : (windowWidth || width) * 0.8;
      const gemprintWidth = Math.min(baseWidth, 1000);
      const gemprintHeight = Math.min(Math.floor(gemprintWidth * 0.7), 700);
      return `${gemprintHeight}px`;
    }
    return theme.isMobile
      ? type === "video" && Platform.OS !== "web"
        ? `${width + 74}px`
        : `${width}px`
      : theme.isLargeScreen
      ? `${width}px`
      : `${height}px`;
  }};
  ${({ theme, isGemprint, containerWidth }) =>
    theme.isLargeScreen &&
    !isGemprint &&
    !containerWidth &&
    css`
      margin: 0 ${SpaceType.Big}px;
      aspect-ratio: 4 / 3;
    `}
  ${({ isGemprint, theme }) =>
    isGemprint &&
    css`
      display: flex;
      justify-content: center;
      overflow: visible;
      ${theme.isTablet && Platform.OS !== "web" && `align-self: center;`}
    `}
`;

const ImageParentContainer = styled.View`
  flex-direction: row;
  align-items: center;
`;

// const CounterContainer = styled.View`
//   margin: 0;
//   align-items: center;
// `;

const ThumbContainer = styled.View<any>`
  justify-content: flex-end;
  align-items: ${({ theme }) => (theme.isMobile ? "left" : "center")};
  margin: ${SpaceType.Basic}px;
  position: relative;
  bottom: 0;
  ${({ theme }) =>
    theme.isMobile &&
    css`
      flex-shrink: 0;
    `}
`;

const ThumbnailsCarousel = styled.ScrollView<{ thumbSize?: number }>`
  overflow: visible;
  ${({ thumbSize }) =>
    thumbSize &&
    css`
      height: ${thumbSize}px;
    `}
`;

const CenteredView = styled.View<any>`
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme["core-02"]};
  height: 100%;
`;

const ModalView = styled(View)`
  flex: 1;
  border-radius: 22px;
  width: 100%;
  height: 100%;
`;

const ClosedView = styled.TouchableOpacity`
  top: 0;
  position: absolute;
  right: 0;
  z-index: 1;
`;

const ClosedContainerView = styled(View)`
  padding: ${SpaceType.Extra}px;
`;

const IconContainer = styled.View<any>`
  padding: 4px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 40px;
`;

const StyledBackdropView = styled.View`
  width: 100vw;
  height: 100vh;
`;

export default GalleryViewModal;
